import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery, useUpdateUserProfileMutation } from '@/features/user/userApi';
import { FolioDrawer } from '@/components/Drawers/FolioDrawer';
import { getPrimaryEmail } from '@/components/views/SettingsView/BrokerageUsersTable/helpers';
import { UserProfileDrawerForm } from './UserProfileDrawerForm';
import { closeDialog } from '@/features/dialog/dialogSlice';

export const UserProfileDrawer = () => {
	const dispatch = useDispatch();

	const open = useSelector((state) => state.dialog?.open?.userProfile) ?? false;
	const dialogData = useSelector((state) => state.dialog?.data?.userProfile);

	const profileUserId = dialogData?.userId;

	const account = useSelector((state) => state.msalAccount.account);
	const loggedInEmail = account?.username;
	const [updateUserProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

	const { data: currentUser } = useGetUserDetailsQuery(
		{ email: loggedInEmail },
		{ skip: !loggedInEmail },
	);

	const {
		data: profileUser,
		isLoading: isLoadingProfile,
		isUninitialized: isProfileUninitialized,
		isError: isProfileError,
	} = useGetUserDetailsQuery({ userId: profileUserId }, { skip: !open || !profileUserId });

	const isSelf = Boolean(
		currentUser?.userId &&
		profileUser?.userId &&
		String(currentUser.userId) === String(profileUser.userId),
	);

	const canEdit = Boolean(isSelf || currentUser?.principleUser);

	const initialValues = useMemo(
		() => ({
			userId: profileUser?.userId ?? '',
			name: profileUser?.name ?? '',
			imageUrl: profileUser?.imageUrl ?? '',
			email: profileUser?.email ?? '',
			proxyAddresses: profileUser?.proxyAddresses ?? [],
			displayEmail: profileUser?.displayEmail ?? getPrimaryEmail(profileUser) ?? '',
			delegatedEmails: profileUser?.delegatedEmails ?? [],
			phoneNumber: profileUser?.phoneNumber ?? '',
			linkedIn: profileUser?.linkedIn ?? '',
			disclosureUrl: profileUser?.disclosureUrl ?? '',
			aboutUser: profileUser?.aboutUser ?? '',
			fspNumber: profileUser?.fspNumber ?? '',
			role: profileUser?.role || '',
			brokerGroupId: profileUser?.brokerGroupId ?? '',
			brokerGroupIds: profileUser?.brokerGroupArray ?? [],
			emailSignatures: profileUser?.emailSignatures ?? [],
			signatureHtmlUrl: profileUser?.signatureHtmlUrl ?? '',
			signatureHtml: '',
		}),
		[profileUser],
	);

	const handleClose = () => {
		dispatch(closeDialog('userProfile'));
	};

	const handleSubmit = async (values) => {
		if (!canEdit || !values?.email) return;
		try {
			await updateUserProfile({
				email: values.email,
				properties: {
					phoneNumber: values.phoneNumber,
					linkedIn: values.linkedIn,
					disclosureUrl: values.disclosureUrl,
					aboutUser: values.aboutUser,
					fspNumber: values.fspNumber,
					role: values.role,
					brokerGroupArray: values.brokerGroupIds ?? [],
					...(isSelf && {
						displayEmail:
							typeof values.displayEmail === 'string'
								? values.displayEmail
								: (values.displayEmail?.name ?? ''),
					}),
					delegatedEmails: values.delegatedEmails ?? [],
				},
			}).unwrap();
			dispatch(showToast({ message: 'Changes saved successfully.', success: true }));
		} catch (err) {
			console.error('Failed to save user profile:', err);
			dispatch(
				showToast({
					message: 'There was an issue saving changes; please refresh and try again.',
					error: true,
				}),
			);
		}
	};

	return (
		<FolioDrawer open={open} onClose={handleClose} title='User profile'>
			{isLoadingProfile || isProfileUninitialized ? (
				<Box display='flex' justifyContent='center' py={4}>
					<CircularProgress />
				</Box>
			) : isProfileError || !profileUser ? (
				<Typography sx={{ py: 2 }}>Unable to load this user profile.</Typography>
			) : (
				<Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
					<UserProfileDrawerForm canEdit={canEdit} isSelf={isSelf} isSaving={isSaving} />
				</Formik>
			)}
		</FolioDrawer>
	);
};

export default UserProfileDrawer;
